import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, Users, ShieldCheck } from "lucide-react";
import { SectionHeader, Badge, Panel, KpiCard } from "@/components/ui/exec";

const REASON_LABEL = {
  paid: "Pagada", trial: "Prueba", admin_grant: "Liberada por admin",
  trial_expired: "Prueba vencida", none: "Sin suscripción", license_disabled: "Licencia desactivada",
};
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "—";

export default function EmpresasView() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);

  const load = async () => {
    try { const { data } = await api.get("/admin/companies"); setCompanies(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { if (user) load(); }, [user]);

  const toggle = async (c, active) => {
    try {
      await api.post(`/admin/companies/${c.id}/license`, { active, note: active ? "Licencia activada por plataforma" : "Licencia desactivada por plataforma" });
      toast.success(active ? `Licencia activada: ${c.name}` : `Licencia desactivada: ${c.name}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const activas = companies.filter((c) => c.access?.active).length;

  return (
    <div className="flex-1 min-h-0 flex flex-col" data-testid="empresas-view">
      <SectionHeader icon={Building2} title="Empresas · Plataforma" subtitle="Control de licencias de todas las empresas registradas. El interruptor activa o desactiva el acceso completo sin necesidad de pago." />
      <div className="flex-1 overflow-auto p-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-3xl">
          <KpiCard label="Empresas" value={companies.length} accent="hsl(212 92% 36%)" icon={Building2} testId="kpi-empresas" />
          <KpiCard label="Licencias activas" value={activas} accent="hsl(160 84% 30%)" icon={ShieldCheck} testId="kpi-licencias-activas" />
          <KpiCard label="Usuarios totales" value={companies.reduce((s, c) => s + (c.users || 0), 0)} accent="hsl(36 96% 44%)" icon={Users} testId="kpi-usuarios-total" />
        </div>
        <Panel className="overflow-hidden">
          <table className="qc-table">
            <thead>
              <tr>
                <th>Empresa</th>
                <th style={{ width: 130 }}>RFC</th>
                <th style={{ width: 80 }} className="text-right">Usuarios</th>
                <th style={{ width: 110 }}>Alta</th>
                <th style={{ width: 170 }}>Estado</th>
                <th style={{ width: 120 }}>Vence</th>
                <th style={{ width: 150 }} className="text-center">Licencia</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => {
                const active = !!c.access?.active;
                const own = c.id === user?.company_id;
                return (
                  <tr key={c.id} data-testid={`empresa-row-${c.id}`}>
                    <td className="text-sm font-medium">{c.name} {own && <Badge tone="primary" className="ml-1">Tu empresa</Badge>}</td>
                    <td className="font-mono-num text-xs text-muted-foreground">{c.rfc || "—"}</td>
                    <td className="text-right font-mono-num">{c.users}</td>
                    <td className="font-mono-num text-xs text-muted-foreground">{fmtDate(c.created_at)}</td>
                    <td><Badge tone={active ? "success" : "danger"} testId={`estado-${c.id}`}>{REASON_LABEL[c.access?.reason] || c.access?.reason}</Badge></td>
                    <td className="font-mono-num text-xs text-muted-foreground">{fmtDate(c.access?.current_period_end || c.access?.trial_ends_at)}</td>
                    <td className="text-center">
                      <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                        <Switch checked={active} disabled={own} onCheckedChange={(v) => toggle(c, v)} data-testid={`license-toggle-${c.id}`} />
                        <span className={active ? "text-emerald-600 font-semibold" : "text-destructive font-semibold"}>{active ? "ON" : "OFF"}</span>
                      </label>
                    </td>
                  </tr>
                );
              })}
              {companies.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Sin empresas.</td></tr>}
            </tbody>
          </table>
        </Panel>
      </div>
    </div>
  );
}
